import { safeNumberFormat } from '@localization/safeIntl';

/**
 * Formats an amount in minor units as currency, deriving the fraction digits from the
 * currency itself — JPY has none, so a hardcoded 2 would be 100x off.
 *
 * Lives here rather than in `@apps/stays/components/OfferSummary/utils` so callers do not
 * inherit that module's graph (`date-fns`, `date-fns-tz`, `DiscountsProvider`, the onsite
 * charge-name maps) just to format one number. `OfferSummary/utils` re-exports it.
 *
 * Falls back to the raw minor units if the locale or currency is not recognised, so a bad
 * code degrades to something visible instead of throwing.
 */
export const formatMinorUnitsAsCurrency = (
  minorUnits: number,
  currency: string,
  locale: string,
  options?: Readonly<{
    /**
     * Drops the ".00" on a whole amount, so `$10` instead of `$10.00`. Cents still show in full.
     * Assumes the currency's own fraction digits: pair it with a forced `minimumFractionDigits`
     * and the digits you forced would be the ones dropped.
     */
    hideWholeUnitCents?: boolean;
    /**
     * Names the currency instead of drawing its symbol — `USD 100`, never a bare `$100`. Use it
     * wherever the amount is NOT in the reader's own currency, because most locales draw USD, CAD
     * and AUD with the same `$`.
     */
    useCurrencyCode?: boolean;
  }>,
): string => {
  try {
    const currencyDisplay = options?.useCurrencyCode ? 'code' : undefined;
    const formatter = safeNumberFormat(locale, { style: 'currency', currency, currencyDisplay });
    const divisor = Math.pow(10, formatter.resolvedOptions().maximumFractionDigits ?? 2);
    if (options?.hideWholeUnitCents && minorUnits % divisor === 0) {
      return safeNumberFormat(locale, { style: 'currency', currency, currencyDisplay, minimumFractionDigits: 0 }).format(minorUnits / divisor);
    }
    return formatter.format(minorUnits / divisor);
  } catch {
    return String(minorUnits);
  }
};
